import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateCleanerAssignments } from "@/lib/mutationHelpers";

export const useAllCleaners = () => {
  return useQuery({
    queryKey: queryKeys.cleaners.all,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cleaners")
        .select("id, name, user_id")
        .order("name", { ascending: true });

      if (error) throw error;
      return data;
    },
  });
};

export const usePropertyCleanerAssignments = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.cleaners.assignments(propertyId || ''),
    queryFn: async () => {
      // CRITICAL: Validate propertyId before query
      if (!propertyId || propertyId === null || propertyId === undefined || propertyId.trim() === '') {
        console.error("CRITICAL: usePropertyCleanerAssignments called with null/undefined propertyId", { propertyId });
        return [];
      }
      
      // Fetch assignments without embed to avoid 400 (RLS/join); then fetch cleaners by id
      const { data: plainAssignments, error: plainError } = await supabase
        .from("cleaner_assignments")
        .select("id, cleaner_id, property_id")
        .eq("property_id", propertyId);

      if (plainError) throw plainError;
      if (!plainAssignments?.length) return [];

      const cleanerIds = [...new Set(plainAssignments.map((a: { cleaner_id?: string }) => a.cleaner_id).filter(Boolean))] as string[];
      const { data: cleanersData } = await supabase
        .from("cleaners")
        .select("id, name, user_id, company_id")
        .in("id", cleanerIds);

      const cleanerMap = new Map((cleanersData ?? []).map((c: { id: string }) => [c.id, c]));
      const assignmentsWithCleaners = plainAssignments.map((a: { id: string; cleaner_id: string; property_id: string }) => ({
        ...a,
        cleaners: a.cleaner_id ? cleanerMap.get(a.cleaner_id) || null : null,
      }));

      const { data: companyAssignment } = await supabase
        .from("property_cleaning_assignments")
        .select("company_id")
        .eq("property_id", propertyId)
        .maybeSingle();

      const propertyCompanyId = companyAssignment?.company_id || null;

      // Get profiles for all cleaners to get full_name (for display name)
      const cleanerUserIds = assignmentsWithCleaners
        .map((a: any) => a.cleaners?.user_id)
        .filter((id: any): id is string => !!id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", cleanerUserIds.length > 0 ? cleanerUserIds : ['00000000-0000-0000-0000-000000000000']);
      const profileMap = new Map((profiles || []).map(p => [p.id, p.full_name]));

      return assignmentsWithCleaners.map((assignment: any) => {
        if (process.env.NODE_ENV === 'development') {
          if (assignment.property_id === null || assignment.property_id === undefined) {
            const error = "INVALID STATE: cleaner_assignment.property_id is NULL";
            console.error(error, { assignment });
            throw new Error(error);
          }
        }

        const cleaner = assignment.cleaners;
        let assignmentType: 'self' | 'company' | 'owner' = 'self';
        if (cleaner?.company_id) {
          if (propertyCompanyId && cleaner.company_id === propertyCompanyId) {
            assignmentType = 'company';
          } else {
            assignmentType = 'owner';
          }
        } else {
          assignmentType = 'self';
        }
        const displayName = profileMap.get(cleaner?.user_id) || cleaner?.name || "Unknown Cleaner";
        return {
          id: assignment.id,
          cleaner_id: assignment.cleaner_id,
          assignment_type: assignmentType,
          cleaner: cleaner ? {
            id: cleaner.id,
            name: displayName,
            user_id: cleaner.user_id,
            company_id: cleaner.company_id,
          } : null,
        };
      });
    },
    enabled: !!propertyId,
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: [], // Show empty array while loading to avoid loading state
  });
};

export const useAssignCleaner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      cleanerId,
    }: {
      propertyId: string;
      cleanerId: string;
    }) => {
      // CRITICAL: Validate both IDs before insert
      if (!propertyId || propertyId === null || propertyId === undefined || propertyId.trim() === '') {
        throw new Error("CRITICAL: Attempted cleaner assignment without propertyId");
      }

      if (!cleanerId || cleanerId === null || cleanerId === undefined || cleanerId.trim() === '') {
        throw new Error("CRITICAL: Attempted cleaner assignment without cleanerId");
      }

      // DEV-ONLY: Log assignment attempt for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('[useAssignCleaner] Inserting cleaner assignment', { propertyId, cleanerId });
      }

      const { error } = await supabase.from("cleaner_assignments").insert({
        property_id: propertyId,
        cleaner_id: cleanerId,
      });

      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (data) => {
      invalidateCleanerAssignments(queryClient, data.propertyId);
    },
  });
};

export const useUnassignCleaner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      cleanerId,
    }: {
      propertyId: string;
      cleanerId: string;
    }) => {
      // Step 1: Check if property has a cleaning company assigned
      // Only regenerate code if NO company is assigned (solo cleaner removal)
      const { data: companyAssignment } = await supabase
        .from("property_cleaning_assignments")
        .select("company_id")
        .eq("property_id", propertyId)
        .maybeSingle();

      const hasCompany = !!companyAssignment?.company_id;

      // Step 2: Remove cleaner assignment
      const { error } = await supabase
        .from("cleaner_assignments")
        .delete()
        .eq("property_id", propertyId)
        .eq("cleaner_id", cleanerId);

      if (error) throw error;

      // Step 3: If NO company assigned, regenerate property_code for security
      // This invalidates the old code so the removed cleaner cannot rejoin
      if (!hasCompany) {
        // Generate new property code using database function
        // Loop until we get a unique code (very unlikely to need multiple attempts)
        let newCode: string | null = null;
        let attempts = 0;
        const maxAttempts = 10;

        while (!newCode && attempts < maxAttempts) {
          const { data: generatedCode, error: codeError } = await supabase
            .rpc("generate_property_code");

          if (codeError) {
            console.error("Error generating new property code:", codeError);
            break;
          }

          if (generatedCode) {
            const normalizedCode = generatedCode.toUpperCase();
            
            // Check if code already exists
            const { data: existing } = await supabase
              .from("properties")
              .select("id")
              .eq("property_code", normalizedCode)
              .maybeSingle();

            if (!existing) {
              newCode = normalizedCode;
            } else {
              attempts++;
              // Code exists, try again
            }
          }
        }

        if (newCode) {
          // Update property with new code
          const { error: updateError } = await supabase
            .from("properties")
            .update({ property_code: newCode })
            .eq("id", propertyId);

          if (updateError) {
            console.error("Error updating property code:", updateError);
            // Continue even if update fails - cleaner is already removed
          }
        } else {
          console.error("Failed to generate unique property code after", maxAttempts, "attempts");
          // Continue even if code generation fails - cleaner is already removed
        }
      }

      return { propertyId, codeRegenerated: !hasCompany };
    },
    onSuccess: (data) => {
      invalidateCleanerAssignments(queryClient, data.propertyId);
      // Invalidate properties query to refresh property code display
      queryClient.invalidateQueries({ queryKey: ["properties"] });
    },
  });
};
